import type { NamespacedI18nKey, NavEntry } from './manifest';

export interface NavSection {
  /** `undefined` for the leading group of entries that declare no section (Home, and friends). */
  readonly title?: NamespacedI18nKey;
  readonly entries: readonly NavEntry[];
}

/**
 * Groups already-filtered nav entries into the drawer's sections (Berry layout, §B.8).
 *
 * Runs on the output of `ModuleRegistry.visibleNav`, never on raw manifests, so a section whose
 * entries were all filtered away by permissions simply does not exist here — there is no way to
 * render a heading over nothing.
 *
 * Order is manifest order, and a section is anchored at its FIRST entry: if two modules interleave
 * entries that claim the same section, they join the group where it opened rather than opening a
 * second identical heading further down. The alternative — a run-length grouping — produces two
 * "Versicherung" headings from one reordering, which reads as a bug to everyone who sees it.
 */
export function groupNavBySection(entries: readonly NavEntry[]): readonly NavSection[] {
  const sections: { title?: NamespacedI18nKey; entries: NavEntry[] }[] = [];
  const byTitle = new Map<NamespacedI18nKey | '', { entries: NavEntry[] }>();

  for (const entry of entries) {
    const key = entry.section ?? '';
    const existing = byTitle.get(key);
    if (existing !== undefined) {
      existing.entries.push(entry);
      continue;
    }
    const created = { title: entry.section, entries: [entry] };
    sections.push(created);
    byTitle.set(key, created);
  }

  return sections;
}
